import Vue from 'vue'
import i18n from '@/i18n'
import Vuex from 'vuex'
import Api from '@/services/ApiStore'
import * as API from '@/services/API'
import Auth from '@/services/Authentication'
import Sections from '@/store/modules/Sections'
import Pages from '@/store/modules/Pages'
import CRUD from '@/store/modules/CRUD'
import { _ } from 'underscore'
import { languageManager } from '@/mixins/LanguageManager'
import axios from 'axios'
import router from '@/router'
Vue.use(Vuex)

// root state object.
// each Vuex instance is just a single state tree.

const availableLanguages = ['ca', 'es', 'en']
const navigatorLanguage = navigator.language.split('-')[0]

const state = {
  form: {},
  capsule: {},
  originalCapsule: {},
  activeLanguage: _.contains(availableLanguages, navigatorLanguage)
    ? navigatorLanguage
    : 'en',
  availableLanguages,
  collections: [],
  capsules: [],
  partners: []
}

const actions = {
  CHANGE_ACTIVE_LANGUAGE ({ commit, getters, dispatch }, payload) {
    commit('CHANGE_ACTIVE_LANGUAGE', payload)
    getters.isLoggedIn && dispatch('setPreferredLanguage', payload)
  },
  fetchCapsule ({ commit, dispatch, state }, payload) {
    return new Promise((resolve, reject) => {
      API.fetchItem('projects', payload.params.id).then(rslt => {
        state.capsule = rslt.data[0]
        commit('formatCapsule', rslt.data[0])
        resolve(rslt)
      })

      // dispatch('getCapsule', payload)
    })
  },
  getProjectByLanguage ({ commit, state, getters }, payload) {
    return new Promise((resolve, reject) => {
      const aggregate = [
        { $addFields: { id: { $toString: '$_id' } } },
        {
          $match: { [`url-reference.${payload.language}`]: payload.reference }
        }
      ]
      axios
        .post(`api/data/projects/get`, {
          aggregate
        })
        .then(rslt => {
          commit('formatRawProject', {
            project: rslt.data[0],
            language: payload.language
          })
          resolve(state.capsule)
        })
        .catch(err => reject(err))
    })
  },
  fetchCollections ({ commit, state }) {
    return new Promise((resolve, reject) => {
      API.fetchItems('capsule-collection', { status: 'enabled' })
        .then(rslt => {
          commit('SET_COLLECTIONS', rslt.data)
          resolve(state.collections)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  fetchPartners ({ commit, state }) {
    return new Promise((resolve, reject) => {
      API.fetchItems('partner', { status: 'enabled' })
        .then(rslt => {
          commit('SET_PARTNERS', rslt.data)
          resolve(state.partners)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  fetchCapsules ({ commit, state }) {
    return new Promise((resolve, reject) => {
      API.fetchItems('projects', { status: 'enabled' })
        .then(rslt => {
          commit('SET_CAPSULES', rslt.data)
          resolve(state.capsules)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  cleanCapsule ({ commit }) {
    commit('CLEAN_CAPSULE')
  },
  setPreferredLanguage ({ commit }, payload) {
    return API.setPreferredLanguage(payload)
  }
}

// mutations are operations that actually mutates the state.
// each mutation handler gets the entire state tree as the
// first argument, followed by additional payload arguments.
// mutations must be synchronous and can be recorded by plugins
// for debugging purposes.
const mutations = {
  CHANGE_ACTIVE_LANGUAGE (state, value) {
    state.activeLanguage = value
    require('moment').locale(value)
    i18n.locale = value
    if (router.currentRoute.path === '/login') {
      return router.push({
        name: 'home',
        query: { lang: value }
      })
    }
    router.push({
      to: router.currentRoute.path,
      query: { ...router.currentRoute.query, ...{ lang: value } }
    })
  },

  changeActiveLanguage (state, value) {
    state.activeLanguage = value
    require('moment').locale(value)
    i18n.locale = value
  },
  formatCapsule (state, payload) {
    state.capsule = transformCapsule(payload)
  },
  formatRawProject (state, payload) {
    let project = _.mapObject(payload.project, (value, key) => {
      return value[payload.language] ? value[payload.language] : value.default
    })
    state.capsule = project
  },
  updateCapsuleImage (state, payload) {
    state.capsule.mainImage = payload[0].fileUrl
  },
  updateCapsulePartner (state, payload) {
    state.capsule['partner-logo'] = payload[0].fileUrl
  },
  SET_COLLECTIONS (state, payload) {
    state.collections = payload
  },
  SET_PARTNERS (state, payload) {
    state.partners = payload
  },
  SET_CAPSULES (state, payload) {
    state.capsules = payload
  },
  CLEAN_CAPSULE (state) {
    state.capsule = {}
  }
}

// getters are functions
const getters = {
  activeLanguage: state => state.activeLanguage,
  availableLanguages: state => state.availableLanguages,
  formattedCapsules: state => {
    return _.filter(_.map(state.capsules, transformCapsule), capsule => {
      return capsule.status !== 'disabled'
    })
  },
  currentCapsuleId: state => {
    return state.capsule._id
  },
  capsuleMainImage: state => {},
  fieldsTranslations: state => {
    const keys = _.pluck(state.originalCapsule.fields, 'name')
    const values = _.map(
      state.originalCapsule.fields,
      field => field.properties.visibleName
    )
    return _.object(
      keys,
      _.map(values, languageManager.methods.activeLanguage)
    )
  },
  networkColumns: state => state.networkColumns,
  partners (state) {
    return state.partners
  },
  partnersLogos (state) {
    return _.pluck(state.partners, 'partner-logo')
  },
  capsulePartner (state) {
    return capsuleId => {
      let capsule = _.find(state.capsules, { _id: capsuleId })
      if (!capsule) {
        return {}
      }
      let partners = capsule.partner
      let mainPartner = _.filter(state.partners, partner => {
        return _.contains(partners, partner._id)
      })[0]
      return languageManager.methods.transformObject(mainPartner)
    }
  }
}

function transformCapsule (capsule) {
  return languageManager.methods.transformObject(capsule)
  /* const keys = _.pluck(capsule.fields, 'name')
  keys.push('id')
  const values = _.pluck(capsule.fields, 'values')
  values.push(capsule.id)
  return _.object(keys, _.map(values, languageManager.methods.activeLanguage)) */
}

export default new Vuex.Store({
  modules: { Api, Auth, Sections, Pages, CRUD },
  state,
  actions,
  getters,
  mutations
})
